'use strict';

import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDrop } from 'react-dnd';

import Popup from '@components/Widgets/Popup.react';
import ComplianceViolations from '../modals/nutrition/ComplianceViolations.react';
import MealDraggable from './MealDraggable.react';

import MealTypes from '@app/constants/MealTypes';
import {
    useMealPlanData,
    useMealOperations,
    useUIState,
    usePermissions
} from '../contexts';

import './MealCell.scss';

const MealCell = ({
    profile,
    meals,
    offset,
    mealType,
    plan = null
}) => {
    // Get context values from split contexts
    const { analysis } = useMealPlanData();
    const { rescheduleMeals, startReplaceMeal, startAddMeal } = useMealOperations();
    const { hideWarnings, isMobile } = useUIState();
    const permissions = usePermissions();

    // Setup drop target
    const [{ isOver, isOverCurrent }, drop] = useDrop({
        accept: [MealTypes.MEAL],
        drop: (item) => {
            if (item.meals) {
                confirmDrop(item.meals);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            isOverCurrent: monitor.isOver({ shallow: true }),
        }),
    });

    // Confirm drop handler
    const confirmDrop = useCallback((confirmMealsDrop) => {
        rescheduleMeals(confirmMealsDrop, offset, mealType);
    }, [rescheduleMeals, offset, mealType]);

    // Render add button
    const renderAddButton = useCallback(() => {
        if (meals.length || permissions.inhibitSwap) {
            return null;
        }

        return (
            <button
                className="add-recipe-btn"
                onClick={() => startAddMeal(offset, mealType)}
                key="add"
                title="Add a recipe here"
            >
                <i className="icon-plus-thin" />
            </button>
        );
    }, [meals, startAddMeal, offset, mealType, permissions.inhibitSwap]);

    // Render action button
    const renderActionButton = useCallback((violations) => {

        // Check if any violation is a maximum exceeded (need to swap)
        const hasMaxExceeded = violations.some(v => v.type === 'meal_maximum_exceeded');

        let button;
        if (hasMaxExceeded) {
            button = (
                <button
                    className="el-medium-btn el-link-raspberry-btn"
                    onClick={() => startReplaceMeal(offset, mealType, meals)}
                >
                    swap {mealType}
                </button>
            );
        } else {
            // For minimum not met, suggest adding a dish
            button = (
                <button
                    className="el-medium-btn el-link-raspberry-btn"
                    onClick={() => startAddMeal(offset, mealType)}
                >
                    add dish
                </button>
            );
        }

        return (
            <footer className="violation-actions">{button}</footer>
        );
    }, [meals, mealType, offset, startReplaceMeal, startAddMeal, analysis]);

    // Render compliance violations
    const renderComplianceViolations = useCallback(() => {
        const violations = analysis?.violationsByMeal?.[offset]?.[mealType] || [];

        if (!permissions.canViewNutrition || !violations.length || profile.type === "alternate-patient" || hideWarnings) {
            return null;
        }

        return (
            <Popup
                button={<i className="icon-warning1" title="Click to see details" />}
                key="desktop-violations"
                positionClassName="el-popup-left-center"
            >
                <div>
                    <ComplianceViolations mealType={mealType} violations={violations} />
                    {permissions.inhibitSwap ? null : renderActionButton(violations)}
                </div>
            </Popup>
        );
    }, [profile, hideWarnings, mealType, offset, renderActionButton, permissions, analysis]);

    return (
        <div className="day-column" ref={drop}>
            <div className="customizer-meal-cell" data-offset={offset} data-meal-type={mealType} data-hover={isOver}>
                {meals.length ? (
                    <MealDraggable
                        profile={profile}
                        offset={offset}
                        mealType={mealType}
                        meals={meals}
                        hideIcons={permissions.inhibitSwap}
                        disableDnD={permissions.inhibitSwap}
                        plan={plan}
                    />
                ) : null}
                {renderAddButton()}
                {meals.length > 0 ? renderComplianceViolations() : null}
            </div>
        </div>
    );
};

MealCell.propTypes = {
    profile: PropTypes.object.isRequired,
    meals: PropTypes.array.isRequired,
    offset: PropTypes.number.isRequired,
    mealType: PropTypes.string.isRequired,
};

export default MealCell;
